import { use, useCallback, useMemo, useState } from "react";
import { AuthManagerContext } from "../contexts/AuthManager";
import {
    BalanceType,
    formatActivityTypeTitle,
    formatCurrency,
    formatDateForGraphql,
    formatSignedBalance,
    getContractPercentage,
} from "../utils";
import {
    ActivityType,
    AmendmentSimpleSigningStateType,
    AmendmentState,
    ContractSignatureStatus,
    ContractState,
    EarningType,
    FeeCountUnit,
    FeeType,
    Maybe,
    MissionCount,
    MissionDeduction,
    ProfileType,
    Repetition,
    RepetitionType,
    TerminateReason,
    TerminationInfo,
} from "../__generated__/graphql";
import {
    CoreContractFieldsFragment,
    CoreMissionFieldsFragment,
    MissionAmendmentFieldsFragment,
} from "../__generated__/operations/graphql";
import { ErrorLike } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
    DELETE_MISSION,
    GET_MISSION_WITH_TERMINATION,
    GET_TERMINATION_INFO,
    TERMINATE_MISSION,
} from "../graphql/core";
import { LabelMode } from "../ui_atoms/Label";
import { useDurationShortFormatter, useI18n } from "./i18n-hooks";
import { FIND_MY_MISSIONS } from "../graphql/home";
import { IconName, hasIcon } from "../components/Icon";
import { useContractSigningInfo } from "./contacts";
import { GET_MISSION_OVERTIME_STATUS, GET_MISSION_VACATION_STATUS } from "../graphql/timesheet";
import { SHORT_DATE, useDateFormatter, useDateRangeFormatter } from "./date";
import { GET_MISSION_AMENDMENTS } from "../graphql/amendment";

export const useActivityTypeFormatter = () => {
    const { user } = use(AuthManagerContext);
    const result = useCallback(
        (activityType: Pick<ActivityType, "title">) => {
            if (!user?.gender) {
                throw new Error("User's gender is not defined");
            }
            return formatActivityTypeTitle(activityType.title, user.gender);
        },
        [user],
    );
    return result;
};

export type MissionAction = "delete" | "terminate" | "close";

type MissionActionFunction = (
    action: MissionAction,
    mission: CoreMissionFieldsFragment,
    reason: TerminateReason,
    terminationDate?: Date,
    otherReason?: string,
) => void | Promise<void>;

type MissionHookResult = {
    onAction: MissionActionFunction;
    processingAction: MissionAction | null;
    successAction: MissionAction | null;
    error: ErrorLike | null;
    reset: () => void;
};

export const useMissionHandler = (
    onActionSuccess?: (
        action: MissionAction,
        mission: CoreMissionFieldsFragment,
    ) => void | Promise<void>,
): MissionHookResult => {
    const [processingAction, setProcessingAction] = useState<MissionAction | null>(null);
    const [deleteMission, { error: deleteError, data: deleteData, reset: deleteReset }] =
        useMutation(DELETE_MISSION);
    const [
        terminateMission,
        { error: terminateError, data: terminateData, reset: terminateReset },
    ] = useMutation(TERMINATE_MISSION, {
        refetchQueries: [
            FIND_MY_MISSIONS,
            GET_TERMINATION_INFO,
            GET_MISSION_AMENDMENTS,
            GET_MISSION_WITH_TERMINATION,
        ],
    });

    const successAction = useMemo(() => {
        if (deleteData) {
            return "delete";
        }
        if (terminateData) {
            return "terminate";
        }
        return null;
    }, [deleteData, terminateData]);

    const error = deleteError || terminateError || null;
    const reset = useCallback(() => {
        setProcessingAction(null);
        deleteReset();
        terminateReset();
    }, [deleteReset, terminateReset]);

    const onAction = useCallback<MissionActionFunction>(
        async (action, mis, reason, terminationDate, otherReason) => {
            reset();
            setProcessingAction(action);
            try {
                switch (action) {
                    case "delete":
                        await deleteMission({ variables: { missionId: mis.id } });
                        break;
                    case "terminate":
                        if (!terminationDate) {
                            terminationDate = new Date();
                        }
                        await terminateMission({
                            variables: {
                                missionId: mis.id,
                                data: {
                                    reason,
                                    terminationDate: formatDateForGraphql(terminationDate),
                                    otherReason: otherReason,
                                },
                            },
                        });
                        break;
                    default:
                        break;
                }
                if (onActionSuccess) {
                    await onActionSuccess(action, mis);
                }
            } finally {
                setProcessingAction(null);
            }
        },
        [reset, onActionSuccess, deleteMission, terminateMission],
    );

    return {
        onAction,
        processingAction,
        successAction,
        error,
        reset,
    };
};

type MissionAttribute = {
    labelMode: LabelMode;
};

const CONTRACT_STATE_MODE_MAP: Record<ContractState, MissionAttribute> = {
    accepted: { labelMode: "success" },
    terminated: { labelMode: "warning" }, // terminated is when contract is in progress but will be ended prematurally
    cancelled: { labelMode: "error" },
    draft: { labelMode: "default" },
    ended: { labelMode: "info" },
    pendingAcceptation: { labelMode: "warning" },
};
const AMENDMEND_STATE_MODE_MAP: Record<AmendmentState, MissionAttribute> = {
    accepted: { labelMode: "success" },
    rejected: { labelMode: "error" },
    applied: { labelMode: "success" },
    canceled: { labelMode: "error" },
    draft: { labelMode: "default" },
    pendingAcceptation: { labelMode: "warning" },
};

// short is a short status that summarize the most important status of contract. it is used as label in mission list for example
// signing is the signing status
// running is the running status
type ContractLabelMode = "short" | "signing" | "running";

export const useComputeAmenmentStatusDisplay = () => {
    const i18n = useI18n();

    return useCallback<
        (
            amendment?: MissionAmendmentFieldsFragment | null,
        ) => { text: string; mode: LabelMode } | undefined
    >(
        (amendment) => {
            const amendmentState = amendment?.state;

            if (amendmentState) {
                const shortLabel = i18n.t(`mission.details.label.short.${amendmentState}`);

                return {
                    text: shortLabel,
                    mode: AMENDMEND_STATE_MODE_MAP[amendmentState]?.labelMode,
                };
            }
        },
        [i18n],
    );
};

export const useComputeContractStatusDisplay = (mode: ContractLabelMode) => {
    const i18n = useI18n();
    const formatDate = useDateFormatter();
    const { getStatusText } = useContractSigningInfo();

    return useCallback<
        (
            contract?: CoreContractFieldsFragment | null,
        ) => { text: string; mode: LabelMode } | undefined
    >(
        (contract) => {
            const contractState = contract?.state;
            const contractStartDate = contract?.startDate;
            if (contractState) {
                if (mode === "signing") {
                    // when contract has not been fully signed, we always display the signing status
                    return {
                        text: getStatusText(contract),
                        mode:
                            contract.signatureStatus === ContractSignatureStatus.Success
                                ? "success"
                                : "warning",
                    };
                }

                if (
                    mode === "short" &&
                    contract.state !== ContractState.Cancelled &&
                    (!contract.signatureStatus ||
                        contract.signatureStatus !== ContractSignatureStatus.Success)
                ) {
                    // when contract has not been fully signed, we always display the signing status
                    return {
                        text: getStatusText(contract),
                        mode: "warning",
                    };
                }

                if (
                    [ContractState.Accepted, ContractState.Terminated].includes(contractState) &&
                    contractStartDate &&
                    new Date(contractStartDate) > new Date()
                ) {
                    return {
                        text: i18n.t("mission.details.will_start_at", {
                            date: formatDate(new Date(contractStartDate), SHORT_DATE),
                        }),
                        mode: CONTRACT_STATE_MODE_MAP[contractState].labelMode,
                    };
                }

                const defaultLabel = i18n.t(`mission.details.label.default.${contractState}`);
                const shortLabel = i18n.t(`mission.details.label.short.${contractState}`);

                let finalText = defaultLabel;

                if (mode === "short") {
                    finalText = shortLabel || defaultLabel;
                }

                return {
                    text: finalText,
                    mode: CONTRACT_STATE_MODE_MAP[contractState]?.labelMode,
                };
            }
        },
        [formatDate, getStatusText, i18n, mode],
    );
};

export const useMissionDisplayAttributes = () => {
    const displayContractAttribute = useContractDisplayAttributes();

    return useCallback(
        (
            mission?: CoreMissionFieldsFragment | null,
            profileType?: ProfileType | null,
            terminationInfo?: TerminationInfo,
        ) => {
            const contractAttributes = displayContractAttribute(
                mission,
                profileType,
                terminationInfo,
            );
            return { ...contractAttributes };
        },
        [displayContractAttribute],
    );
};

const FEE_ICONS: Record<FeeType, IconName> = {
    [FeeType.Cct]: "cct",
    [FeeType.Other]: "fee-other",
    [FeeType.Food]: "fee-food",
    [FeeType.Phone]: "fee-phone",
    [FeeType.RemoteWork]: "fee-remote-work",
    [FeeType.PrivateTransport]: "fee-private-transportation",
    [FeeType.PublicTransport]: "fee-public-transportation",
};

export const useContractDisplayAttributes = () => {
    const intDateFormatter = useDateFormatter();
    const i18n = useI18n();

    const formatDate = useCallback(
        (d?: string | null, empty = "") => {
            if (!d) {
                return empty;
            }
            return intDateFormatter(new Date(d), {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        [intDateFormatter],
    );
    const formatDateRange = useDateRangeFormatter();
    const computeRunningStatusDisplay = useComputeContractStatusDisplay("running");
    const computeSigningStatusDisplay = useComputeContractStatusDisplay("signing");
    const computeShortStatusDisplay = useComputeContractStatusDisplay("short");

    return useCallback(
        (
            mission?: CoreMissionFieldsFragment | null,
            profileType?: ProfileType | null,
            terminationInfo?: TerminationInfo,
        ) => {
            const contract = mission?.currentContract;
            const buildRepetitionKey = (
                repetition: RepetitionType,
                countUnit?: FeeCountUnit | null,
            ) => {
                if (
                    contract?.workingTimeClassicSpec?.repetition === Repetition.None &&
                    repetition === RepetitionType.WorkedMonth
                ) {
                    return i18n.t("fees.repetition.total");
                }
                if (
                    repetition === RepetitionType.Count &&
                    countUnit &&
                    countUnit !== FeeCountUnit.Default
                ) {
                    // A non-default count unit (e.g. km) overrides the generic "per occurrence" label.
                    return i18n.t(`fees.repetition.count_${countUnit}`);
                }
                return i18n.t(`fees.repetition.${repetition}`);
            };

            if (contract && profileType) {
                // Mission-level dates span the whole mission: they cover the signed contracts as
                // a whole, including a signed amendment the cron has not applied yet, where the
                // current contract only knows its own period.
                const startAt = formatDate(mission.startDate);
                const effectiveEndAt = formatDate(mission.effectiveEndDate);
                const duration =
                    mission.startDate && mission.endDate
                        ? formatDateRange(
                              new Date(mission.startDate),
                              new Date(mission.endDate),
                              "long",
                          )
                        : i18n.t("mission.duration_without_end", { start: startAt });

                const effectiveDurationInfo =
                    mission.startDate && mission.effectiveEndDate
                        ? formatDateRange(
                              new Date(mission.startDate),
                              new Date(mission.effectiveEndDate),
                              "long",
                          )
                        : i18n.t("mission.duration_without_end", { start: startAt });
                const terminationDate = effectiveEndAt
                    ? i18n.t("mission.terminationDate", { date: effectiveEndAt })
                    : undefined;
                let salaryGross = "";
                let salaryInvoice = "";
                if (contract.grossSalary && contract.invoiceSalary) {
                    salaryGross = i18n.t(`mission.salary_${contract.earningType}`, {
                        value: formatCurrency({ amountInCents: contract.grossSalary }),
                    });
                    salaryInvoice = i18n.t(`mission.salary_${contract.earningType}`, {
                        value: formatCurrency({ amountInCents: contract.invoiceSalary }),
                    });
                }
                const workPercentage = getContractPercentage(contract) || "";

                const newTerminationState = terminationInfo?.newState;

                const isAlreadyTerminal = [
                    ContractState.Cancelled,
                    ContractState.Terminated,
                    ContractState.Ended,
                ].includes(contract.state);
                const isCancellable =
                    !isAlreadyTerminal &&
                    !terminationInfo?.cancellationRequest &&
                    newTerminationState === ContractState.Cancelled;
                const isTerminable =
                    !isAlreadyTerminal &&
                    !terminationInfo?.cancellationRequest &&
                    newTerminationState === ContractState.Terminated;

                const canCancelOrTerminate = isCancellable || isTerminable;

                const hasPendingCurrentUserSignatureForAmendment =
                    (profileType === ProfileType.Client &&
                        mission.amendmentSigningState ===
                            AmendmentSimpleSigningStateType.WaitingClient) ||
                    (profileType === ProfileType.Worker &&
                        mission.amendmentSigningState ===
                            AmendmentSimpleSigningStateType.WaitingWorker);
                const hasPendingWorkerSignatureForAmendment =
                    profileType === ProfileType.Client &&
                    mission.amendmentSigningState === AmendmentSimpleSigningStateType.WaitingWorker;

                // The back-end keeps reporting a contract as cancellable after a party has signed
                // it, so "cancellable" alone is not enough: re-opening the creation wizard on a
                // signed contract would rewrite something someone already agreed to.
                const isAlreadySigned = [
                    ContractSignatureStatus.NeedOtherSign,
                    ContractSignatureStatus.NeedSeal,
                    ContractSignatureStatus.Success,
                    ContractSignatureStatus.HandwrittenInProcess,
                ].includes(contract.signatureStatus);

                const canUpdate =
                    isCancellable && profileType === ProfileType.Client && !isAlreadySigned;
                const canEdit =
                    isTerminable &&
                    profileType === ProfileType.Client &&
                    !hasPendingCurrentUserSignatureForAmendment &&
                    !hasPendingWorkerSignatureForAmendment &&
                    contract.earningType !== EarningType.Package;

                const hasExistingPendingAvenant =
                    mission.amendmentSigningState &&
                    [
                        AmendmentSimpleSigningStateType.WaitingBasquiat,
                        AmendmentSimpleSigningStateType.WaitingClient,
                        AmendmentSimpleSigningStateType.WaitingWorker,
                    ].includes(mission.amendmentSigningState);

                const isMissionAwaitingWorkerSignature =
                    profileType === ProfileType.Client &&
                    contract.signatureStatus === ContractSignatureStatus.NeedOtherSign;

                const canDuplicate = profileType === ProfileType.Client;

                const runningStatusDisplay = computeRunningStatusDisplay(contract);
                const shortStatusDisplay = computeShortStatusDisplay(contract);
                const signingStatusDisplay = computeSigningStatusDisplay(contract);

                return {
                    durationInfo: duration,
                    effectiveDurationInfo: effectiveDurationInfo,
                    hasEffectiveEndDate: !!mission.effectiveEndDate,
                    terminationDate: terminationDate,
                    salaryGross,
                    salaryInvoice,
                    workPercentage: workPercentage ? `${workPercentage}%` : "",
                    runningStatus: runningStatusDisplay?.text,
                    runningStatuslabelMode: runningStatusDisplay?.mode,
                    signingStatus: signingStatusDisplay?.text,
                    signingStatuslabelMode: signingStatusDisplay?.mode,
                    shortStatus: shortStatusDisplay?.text,
                    shortStatuslabelMode: shortStatusDisplay?.mode,
                    fees: (contract.fees || []).map((value) => ({
                        key: `${value.type}-${value.externalId}-${value.otherTitle}`,
                        icon: hasIcon(value.externalId || "")
                            ? (value.externalId as IconName)
                            : FEE_ICONS[value.type],
                        title:
                            value.type === FeeType.Other
                                ? i18n.t("fees.type.otherText.title", {
                                      text: value.otherTitle || "???",
                                  })
                                : value.type === FeeType.Cct
                                  ? i18n.t(`fees.cct.${value.externalId}_title`)
                                  : i18n.t(`fees.type.${value.type}.title`),
                        description: `${formatCurrency({
                            amountInCents:
                                profileType === ProfileType.Client
                                    ? value.invoiceAmount || value.amount
                                    : value.amount,
                        })} ${buildRepetitionKey(value.repetition, value.countUnit)}`,
                    })),
                    deductions: (contract.deductions || []).map((value) => ({
                        key: `${value.description}-${value.externalId}`,
                        title: i18n.t("deduction.title", { description: value.description }),
                        description: `${formatCurrency({
                            amountInCents: value.amount,
                        })} ${buildRepetitionKey(value.repetition)}`,
                    })),
                    vacationDays: i18n.t("mission.vacationDaysValue", {
                        days: contract.vacationDays || 0,
                    }),
                    canCancelOrTerminate,
                    canEdit,
                    canUpdate,
                    canDuplicate,
                    hasExistingPendingAvenant,
                    hasPendingCurrentUserSignatureForAmendment,
                    hasPendingWorkerSignatureForAmendment,
                    isMissionAwaitingWorkerSignature,
                };
            } else {
                return {
                    ...CONTRACT_STATE_MODE_MAP["draft"],
                    durationInfo: "",
                    effectiveDurationInfo: "",
                    hasEffectiveEndDate: false,
                    terminationDate: "",
                    salaryGross: "",
                    salaryInvoice: "",
                    workPercentage: "",
                    vacationDays: "",
                    defaultStatus: i18n.t("mission.details.draft_title"),
                    shortStatus: i18n.t("contract.details.label.unknown"),
                    fees: [],
                    deductions: [],
                    canCancelOrTerminate: false,
                    canEdit: false,
                    canUpdate: false,
                    canDuplicate: false,
                    hasExistingPendingAvenant: false,
                    hasPendingCurrentUserSignatureForAmendment: false,
                    hasPendingWorkerSignatureForAmendment: false,
                    isMissionAwaitingWorkerSignature: false,
                };
            }
        },
        [
            i18n,
            formatDate,
            formatDateRange,
            computeRunningStatusDisplay,
            computeSigningStatusDisplay,
            computeShortStatusDisplay,
        ],
    );
};

export const useRelevantMissionCounter = () => {
    return useCallback((missionCount?: Maybe<MissionCount>) => {
        if (!missionCount) {
            return 0;
        }
        return missionCount.ended + missionCount.accepted + missionCount.terminated;
    }, []);
};

export const useMissionHolidayBalance = (missionId?: string) => {
    const skip = !missionId;
    const {
        data,
        loading,
        refetch: _refetch,
    } = useQuery(GET_MISSION_VACATION_STATUS, {
        variables: { missionId: missionId || "" },
        skip,
        fetchPolicy: "cache-and-network",
    });
    const formatDuration = useDurationShortFormatter();
    const formatDate = useDateFormatter();
    const refetch = useMemo(() => (skip ? () => {} : _refetch), [skip, _refetch]);

    return useMemo(() => {
        if (!missionId) {
            return { refetch, balance: undefined };
        }
        const balance: [boolean, BalanceType] = [loading, { text: "", date: undefined }];
        if (data) {
            const vacationAmountMinutes = data.getMissionVacationStatus.availableVacationMinutes;
            const dailyHours =
                parseFloat(data.getMissionVacationStatus.contractHoursPerWeekFullTime) / 5;
            const accountingDate = data.getMissionVacationStatus.accountingDate;
            if (accountingDate) {
                balance[1].date = formatDate(new Date(accountingDate));
            }
            balance[1].text = formatSignedBalance(vacationAmountMinutes, (minutes) =>
                formatDuration(minutes, dailyHours),
            );
        }
        return { refetch, balance };
    }, [data, formatDate, formatDuration, loading, missionId, refetch]);
};

export const useMissionOvertimeBalance = (missionId?: string) => {
    const skip = !missionId;
    const {
        data,
        loading,
        refetch: _refetch,
    } = useQuery(GET_MISSION_OVERTIME_STATUS, {
        variables: { missionId: missionId || "" },
        skip: !missionId,
        fetchPolicy: "cache-and-network",
    });
    const formatDuration = useDurationShortFormatter();
    const formatDate = useDateFormatter();
    const refetch = useMemo(() => (skip ? () => {} : _refetch), [skip, _refetch]);
    return useMemo(() => {
        if (!missionId) {
            return { refetch, balance: undefined };
        }
        const balance: [boolean, BalanceType] = [loading, { text: "", date: undefined }];
        if (data) {
            const overtimeMinutes = data.getMissionOvertimeStatus.overtimeMinutes;
            const accountingDate = data.getMissionOvertimeStatus.accountingDate;
            if (accountingDate) {
                balance[1].date = formatDate(new Date(accountingDate));
            }
            balance[1].text = formatSignedBalance(overtimeMinutes, (minutes) =>
                formatDuration(minutes),
            );
        }
        return { refetch, balance };
    }, [data, formatDate, formatDuration, loading, missionId, refetch]);
};

export const useBuildMissionDeductionDescription = () => {
    const i18n = useI18n();

    return useCallback(
        ({
            deductions,
            isPackage,
        }: {
            deductions?: Array<MissionDeduction> | null;
            isPackage: boolean;
        }): string => {
            const byRepetition = {
                [RepetitionType.Count]: [] as Array<number>,
                [RepetitionType.WorkedMonth]: 0,
                [RepetitionType.WorkedDay]: 0,
            };
            for (const deduction of deductions || []) {
                if (deduction.repetition === RepetitionType.Count) {
                    byRepetition[deduction.repetition].push(deduction.amount);
                } else {
                    byRepetition[deduction.repetition] += deduction.amount;
                }
            }
            const description = [];
            if (byRepetition[RepetitionType.Count].length > 0) {
                for (const amount of byRepetition[RepetitionType.Count]) {
                    description.push(
                        `${formatCurrency({ amountInCents: amount })} ${i18n.t("fees.repetition.count")}`,
                    );
                }
            }
            if (byRepetition[RepetitionType.WorkedMonth] > 0) {
                description.push(
                    `${formatCurrency({ amountInCents: byRepetition[RepetitionType.WorkedMonth] })} ${isPackage ? i18n.t("fees.repetition.total") : i18n.t("fees.repetition.worked_month")}`,
                );
            }
            if (byRepetition[RepetitionType.WorkedDay] > 0) {
                description.push(
                    `${formatCurrency({ amountInCents: byRepetition[RepetitionType.WorkedDay] })} ${i18n.t("fees.repetition.worked_day")}`,
                );
            }
            return description.join("\n");
        },
        [i18n],
    );
};
